import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { EMPTY, forkJoin, from, merge, of } from "rxjs";
import {
  map,
  catchError,
  switchMap,
  tap,
  withLatestFrom,
  take,
  mergeMap,
  filter,
  delay,
  takeUntil,
} from "rxjs/operators";
import { Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MipsPacketActions } from "@store/actions/mips-packet/mips-packet.actions";
import { MipsApiService, parseAttestationBool, QualityMeasureScoreInputDto } from "@services/mips-api.service";
import { openBlobInNewTab, downloadBlob } from "../../../utils/blob-viewer.util";
import { enrichQualityData } from "../../../utils/quality-measure-enrichment.util";
import { parseProblemDetailsError } from "../../../utils/api-error.util";
import {
  selectActivePacket,
  selectPacketId,
  selectLastQualityMeasureClinicians,
  selectProvidersForSelectedPractice,
  selectTinDetails,
  selectEffectiveQuestionnaire,
  selectMultiPacketMode,
} from "@store/selectors/mips-packet/mips-packet.selector";
import {
  MipsPacket,
  MipsSettings,
  EligibilitySelection,
  TinEligibilityDetails,
} from "@models/mips-stepper.model";
import { DashboardService } from "@services/dashboard.service";
import { ErrorDialogService } from "@services/error-dialog.service";
import { OrganizationDetailsService } from "@services/organization-details.service";
import { DashboardProviderProd } from "@models/dashboard-providers.model";
import { DashboardMeasureDetailProd } from "@models/dashboard-measure-details.model";
import { selectMvpId } from "@store/selectors/mips-eligibility/mips-eligibility.selectors";
import { getMipsPerformanceDates } from "../../../constants/mips-constants";
import { applyYearRetirements } from "../../../constants/measure-retirements";
import { selectActiveUser } from "@store/selectors/user/user.selectors";
import { MipsQuestionnaireService } from "@services/mips-questionnaire.service";
import { MipsQuestionnaireActions } from "@store/actions/mips-questionnaire/mips-questionnaire.actions";
import { selectMipsQuestionnaire } from "@store/selectors/mips-questionnaire/mips-questionnaire.selectors";

@Injectable()
export class MipsPacketEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly mipsApi = inject(MipsApiService);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dashboardService = inject(DashboardService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly organizationService = inject(OrganizationDetailsService);
  private readonly questionnaireService = inject(MipsQuestionnaireService);

  getOrCreatePacket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.getOrCreatePacket),
      withLatestFrom(
        this.store.select(selectActivePacket),
        this.store.select(selectMvpId),
      ),
      switchMap(([{ eligibility }, currentPacket, mvpId]) => {
        const cancel$ = this.actions$.pipe(ofType(MipsPacketActions.clearPacket));

        if (eligibility.reportingType === "both") {
          const traditionalEligibility = {
            ...eligibility,
            reportingType: "traditional" as const,
            reportingLevel: eligibility.reportingLevel,
          };
          const mvpEligibility = {
            ...eligibility,
            reportingType: "mvp" as const,
            reportingLevel: eligibility.reportingLevel,
          };

          return this.mipsApi.createSubmission(traditionalEligibility).pipe(
            switchMap((traditionalResponse) =>
              this.mipsApi.createSubmission(mvpEligibility, undefined, undefined, mvpId).pipe(
                switchMap((mvpResponse) =>
                  forkJoin({
                    traditional: this.mipsApi.getSubmission(
                      traditionalResponse.submissionId,
                    ),
                    mvp: this.mipsApi.getSubmission(mvpResponse.submissionId),
                  }).pipe(
                    mergeMap(({ traditional, mvp }) => {
                      const packet = this.mapBackendToFrontend(
                        traditional,
                        traditionalEligibility,
                        currentPacket,
                      );

                      return [
                        MipsPacketActions.setMultiPacketMode({
                          mode: "both",
                          traditionalPacketId: traditionalResponse.submissionId,
                          mvpPacketId: mvpResponse.submissionId,
                          currentType: "traditional",
                          nextType: "mvp",
                        }),
                        MipsPacketActions.getOrCreatePacketSuccess({
                          packet,
                        }),
                      ];
                    }),
                  ),
                ),
              ),
            ),
            catchError((error) =>
              of(
                MipsPacketActions.getOrCreatePacketFailure({
                  error: error.message || "Failed to create submissions",
                }),
              ),
            ),
            takeUntil(cancel$),
          );
        } else {
          return this.mipsApi.createSubmission(eligibility, undefined, undefined, mvpId).pipe(
            switchMap((response) =>
              this.mipsApi.getSubmission(response.submissionId).pipe(
                map((backendSubmission) => {
                  const packet = this.mapBackendToFrontend(
                    backendSubmission,
                    eligibility,
                    currentPacket,
                  );

                  return MipsPacketActions.getOrCreatePacketSuccess({ packet });
                }),
              ),
            ),
            catchError((error) =>
              of(
                MipsPacketActions.getOrCreatePacketFailure({
                  error: error.message || "Failed to create submission",
                }),
              ),
            ),
            takeUntil(cancel$),
          );
        }
      }),
    ),
  );

  loadExistingPacket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.loadExistingPacket),
      withLatestFrom(
        this.store.select(selectActivePacket),
        this.store.select(
          (state) => (state as any).mipsPacket?.multiPacketMode,
        ),
      ),
      switchMap(([{ packetId }, currentPacket, multiPacketMode]) => {

        if (
          multiPacketMode?.enabled &&
          multiPacketMode.traditionalPacketId &&
          multiPacketMode.mvpPacketId
        ) {
          return forkJoin({
            traditional: this.mipsApi.getSubmission(
              multiPacketMode.traditionalPacketId,
            ),
            mvp: this.mipsApi.getSubmission(multiPacketMode.mvpPacketId),
          }).pipe(
            mergeMap(({ traditional, mvp }) => {
              const traditionalPacket = this.mapBackendToFrontend(
                traditional,
                undefined,
                currentPacket,
              );

              const mvpPacket = this.mapBackendToFrontend(
                mvp,
                undefined,
                null,
              );

              const actions: any[] = [];

              if (!multiPacketMode.currentType) {
                actions.push(
                  MipsPacketActions.setMultiPacketMode({
                    mode: "both",
                    traditionalPacketId: multiPacketMode.traditionalPacketId,
                    mvpPacketId: multiPacketMode.mvpPacketId,
                    currentType: "traditional",
                    nextType: "mvp",
                  })
                );
              }

              actions.push(
                MipsPacketActions.loadBothPacketsSuccess({
                  traditionalPacket,
                  mvpPacket,
                })
              );

              return actions;
            }),
            catchError((error) =>
              of(
                MipsPacketActions.loadBothPacketsFailure({
                  error: error.message || "Failed to load submissions",
                }),
              ),
            ),
          );
        } else {
          return this.mipsApi.getSubmission(packetId).pipe(
            map((backendSubmission) => {
              const packet = this.mapBackendToFrontend(
                backendSubmission,
                undefined,
                currentPacket,
              );

              return MipsPacketActions.loadExistingPacketSuccess({ packet });
            }),
            catchError((error) =>
              of(
                MipsPacketActions.loadExistingPacketFailure({
                  error: error.message || "Failed to load submission",
                }),
              ),
            ),
          );
        }
      }),
    ),
  );

  handleLoadedSubmittedPacket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.loadExistingPacketSuccess),
      filter(({ packet }) => packet.status === 'submitted'),
      withLatestFrom(this.store.select(selectTinDetails)),
      map(([{ packet }, tinDetails]) =>
        MipsPacketActions.setSubmissionSuccessData({
          packetId: packet.id!,
          tin: packet.tinDetails?.tin ?? tinDetails?.tin ?? '',
          npi: packet.npi,
          isIndividual: packet.reportingLevel === 'individual',
          performanceYear: packet.performanceYear,
          externalSubmissionId: packet.externalSubmissionId ?? '',
          submittedDate: packet.submittedDate ?? '',
        }),
      ),
    ),
  );

  loadProvidersForSelectedPractice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.loadProvidersForSelectedPractice),
      switchMap(({ orgId }) =>
        this.dashboardService.getDashboardProviders(orgId).pipe(
          map((response: any) => {
            const providers = response as DashboardProviderProd[];
            return MipsPacketActions.loadProvidersForSelectedPracticeSuccess({
              providers,
            });
          }),
          catchError((error) =>
            of(
              MipsPacketActions.loadProvidersForSelectedPracticeFailure({
                error: error?.message || "Failed to load providers",
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadTinsForPractice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.loadTinsForPractice),
      switchMap(({ orgId }) =>
        this.organizationService.getOrganizationDetails(orgId).pipe(
          map((orgDetails: any) =>
            MipsPacketActions.loadTinsForPracticeSuccess({
              tins: orgDetails.organizationTins || [],
            }),
          ),
          catchError((error) =>
            of(
              MipsPacketActions.loadTinsForPracticeFailure({
                error: error?.message || "Failed to load TINs",
              }),
            ),
          ),
        ),
      ),
    ),
  );

  showLoadTinsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsPacketActions.loadTinsForPracticeFailure),
        tap(({ error }) => {
          this.errorDialogService.showError(
            error,
            "Failed to Load TINs"
          );
        })
      ),
    { dispatch: false }
  );

  saveTinDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.saveTINDetails),
      withLatestFrom(
        this.store.select(
          (state) => (state as any).mipsPacket?.multiPacketMode,
        ),
      ),
      switchMap(([{ packetId, tinDetails }, multiPacketMode]) => {
        const traditionalPacketId = multiPacketMode?.traditionalPacketId;
        const mvpPacketId = multiPacketMode?.mvpPacketId;

        if (multiPacketMode?.enabled && traditionalPacketId && mvpPacketId) {
          return this.mipsApi
            .saveTinDetails(traditionalPacketId, tinDetails)
            .pipe(
              switchMap(() =>
                this.mipsApi.saveTinDetails(mvpPacketId, tinDetails).pipe(
                  map((response) => {
                    return MipsPacketActions.saveTINDetailsSuccess({
                      tinDetails: tinDetails,
                    });
                  }),
                ),
              ),
              catchError((error) =>
                of(
                  MipsPacketActions.saveTINDetailsFailure({
                    error: error.message || "Failed to save TIN details",
                  }),
                ),
              ),
            );
        }

        return this.mipsApi.saveTinDetails(packetId, tinDetails).pipe(
          map((response) => {
            return MipsPacketActions.saveTINDetailsSuccess({
              tinDetails: tinDetails,
            });
          }),
          catchError((error) =>
            of(
              MipsPacketActions.saveTINDetailsFailure({
                error: error.message || "Failed to save TIN details",
              }),
            ),
          ),
        );
      }),
    ),
  );

  saveSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.saveSettings),
      withLatestFrom(
        this.store.select(selectPacketId),
        this.store.select(selectActivePacket),
        this.store.select(
          (state) => (state as any).mipsPacket?.multiPacketMode,
        ),
      ),
      switchMap(([{ settings }, packetId, currentPacket, multiPacketMode]) => {
        if (!packetId || !currentPacket) {
          return of(
            MipsPacketActions.saveSettingsFailure({
              error: "No submission ID available",
            }),
          );
        }

        const organizationId = parseInt(currentPacket.orgId, 10);
        const performanceYear = currentPacket.performanceYear;
        const traditionalPacketId = multiPacketMode?.traditionalPacketId;
        const mvpPacketId = multiPacketMode?.mvpPacketId;

        if (multiPacketMode?.enabled && traditionalPacketId && mvpPacketId) {
          return this.mipsApi
            .saveSettings(
              traditionalPacketId,
              settings,
              organizationId,
              performanceYear,
            )
            .pipe(
              switchMap(() =>
                this.mipsApi
                  .saveSettings(
                    mvpPacketId,
                    settings,
                    organizationId,
                    performanceYear,
                  )
                  .pipe(
                    map(() =>
                      MipsPacketActions.saveSettingsSuccess({ settings }),
                    ),
                  ),
              ),
              catchError((error) =>
                of(
                  MipsPacketActions.saveSettingsFailure({
                    error: error.message || "Failed to save settings",
                  }),
                ),
              ),
            );
        }

        return this.mipsApi
          .saveSettings(packetId, settings, organizationId, performanceYear)
          .pipe(
            map(() => MipsPacketActions.saveSettingsSuccess({ settings })),
            catchError((error) =>
              of(
                MipsPacketActions.saveSettingsFailure({
                  error: error.message || "Failed to save settings",
                }),
              ),
            ),
          );
      }),
    ),
  );

  refreshMvpRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.refreshMVPRegistration),
      withLatestFrom(
        this.store.select(selectMultiPacketMode),
        this.store.select(selectActivePacket),
      ),
      switchMap(([, multiPacketMode, activePacket]) => {
        const submissionId = multiPacketMode?.enabled
          ? multiPacketMode.mvpPacketId
          : activePacket?.id;

        if (!submissionId) {
          return of(
            MipsPacketActions.refreshMVPRegistrationFailure({
              error: "No MVP submission ID available",
            }),
          );
        }

        return this.mipsApi.refreshMvpRegistration(submissionId).pipe(
          map((response) =>
            MipsPacketActions.refreshMVPRegistrationSuccess({
              submissionId,
              mvpRegistered: response.mvpRegistered,
              mvpId: response.mvpid,
            }),
          ),
          catchError((error) =>
            of(
              MipsPacketActions.refreshMVPRegistrationFailure({
                error:
                  error.error?.description ||
                  error.message ||
                  "Failed to refresh MVP registration",
              }),
            ),
          ),
        );
      }),
      tap((action) => {
        if (action.type === MipsPacketActions.refreshMVPRegistrationSuccess.type) {
          this.snackBar.open(
            action.mvpRegistered && action.mvpId
              ? "MVP registration confirmed."
              : action.mvpRegistered
                ? "Registered for MVP, but no MVP ID has been issued yet."
                : "No registration info found.",
            "Close",
            { duration: 5000 },
          );
        } else if (action.type === MipsPacketActions.refreshMVPRegistrationFailure.type) {
          this.snackBar.open(
            action.error || "Failed to refresh MVP registration status.",
            "Close",
            { duration: 5000 },
          );
        }
      }),
    ),
  );

  saveQualityMeasurements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.saveQualityMeasurements),
      withLatestFrom(
        this.store.select(selectPacketId),
        this.store.select(
          (state) => (state as any).mipsPacket?.multiPacketMode,
        ),
      ),
      switchMap(
        ([
          {
            measurements,
            quality,
            reportingType,
            traditionalMeasureIds,
            mvpMeasureIds,
          },
          packetId,
          multiPacketMode,
        ]) => {
          if (!packetId) {
            return of(
              MipsPacketActions.saveQualityMeasurementsFailure({
                error: "No submission ID available",
              }),
            );
          }

          const isBothMode = reportingType === "both";

          const traditionalIds = new Set(quality.traditionalMeasures.map((m) => m.measureId));
          const mvpIds = new Set(quality.mvpMeasures.map((m) => m.measureId));
          const buildMeasureScores = (measureIds: string[]): QualityMeasureScoreInputDto[] =>
            quality.allMeasures
              .filter((m) => measureIds.includes(m.measureId))
              .map((m) => ({
                measureCode: m.measureId,
                calculatedPoints: m.calculatedPoints ?? null,
                isMIPS: traditionalIds.has(m.measureId),
                isMVP: mvpIds.has(m.measureId),
              }));

          if (
            isBothMode &&
            multiPacketMode?.traditionalPacketId &&
            multiPacketMode?.mvpPacketId
          ) {
            const traditionalMeasurements = measurements.filter((m) =>
              traditionalMeasureIds.includes(m.measureId),
            );

            const mvpMeasurements = measurements.filter((m) =>
              mvpMeasureIds.includes(m.measureId),
            );
            const traditionalSave$ = this.mipsApi.saveQualityMeasurements(
              multiPacketMode.traditionalPacketId,
              traditionalMeasurements,
              buildMeasureScores(traditionalMeasureIds),
            );

            const mvpSave$ = this.mipsApi.saveQualityMeasurements(
              multiPacketMode.mvpPacketId,
              mvpMeasurements,
              buildMeasureScores(mvpMeasureIds),
            );

            return forkJoin({
              traditional: traditionalSave$,
              mvp: mvpSave$,
            }).pipe(
              map((responses) => {
                return MipsPacketActions.saveQualityMeasurementsSuccess({
                  response: responses.traditional,
                  mvpResponse: responses.mvp,
                  quality,
                  traditionalMeasureIds,
                  mvpMeasureIds,
                  reportingType,
                });
              }),
              catchError((error) =>
                of(
                  MipsPacketActions.saveQualityMeasurementsFailure({
                    error:
                      error.message || "Failed to save quality measurements",
                  }),
                ),
              ),
            );
          } else {
            return this.mipsApi
              .saveQualityMeasurements(
                packetId,
                measurements,
                buildMeasureScores([...traditionalMeasureIds, ...mvpMeasureIds]),
              )
              .pipe(
                map((response) =>
                  MipsPacketActions.saveQualityMeasurementsSuccess({
                    response,
                    quality,
                    traditionalMeasureIds,
                    mvpMeasureIds,
                    reportingType,
                  }),
                ),
                catchError((error) =>
                  of(
                    MipsPacketActions.saveQualityMeasurementsFailure({
                      error:
                        error.message || "Failed to save quality measurements",
                    }),
                  ),
                ),
              );
          }
        },
      ),
    ),
  );

  savePIAttestations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.savePIAttestations),
      withLatestFrom(
        this.store.select(
          (state) => (state as any).mipsPacket?.multiPacketMode,
        ),
      ),
      switchMap(
        ([
          {
            packetId,
            attestations,
            performanceStart,
            performanceEnd,
            organizationId,
            performanceYear,
            currentMeasures,
            fullMeasures,
            exclusionIds,
          },
          multiPacketMode,
        ]) => {
          const toYesNoOrNull = (val: "yes" | "no" | "" | null): "yes" | "no" | null =>
            val === "yes" ? "yes" : val === "no" ? "no" : null;

          const questionnaireAttestations = {
            piAuditAttestation: attestations.piAuditAttestation === true,
            oncDirectReview: toYesNoOrNull(attestations.oncDirectReview),
            interoperabilityAttestation: toYesNoOrNull(attestations.interoperabilityAttestation),
            securityRiskAnalysis: toYesNoOrNull(attestations.securityRiskAnalysis),
            securityRiskAnalysisImplemented: toYesNoOrNull(attestations.securityRiskAnalysisImplemented),
            saferGuidesAttestation: toYesNoOrNull(attestations.saferGuidesAttestation),
          };

          const allMeasurements = [...(currentMeasures || [])];

          const measurementRequest = {
            performanceStart,
            performanceEnd,
            measurements: allMeasurements,
          };

          const traditionalPacketId = multiPacketMode?.traditionalPacketId;
          const mvpPacketId = multiPacketMode?.mvpPacketId;

          if (multiPacketMode?.enabled && traditionalPacketId && mvpPacketId) {
            return this.mipsApi
              .savePIAttestations(
                traditionalPacketId,
                questionnaireAttestations,
                organizationId,
                performanceYear,
              )
              .pipe(
                switchMap(() =>
                  this.mipsApi
                    .savePIAttestations(
                      mvpPacketId,
                      questionnaireAttestations,
                      organizationId,
                      performanceYear,
                    )
                    .pipe(
                      switchMap(() =>
                        this.mipsApi
                          .savePIMeasurements(
                            traditionalPacketId,
                            measurementRequest,
                          )
                          .pipe(
                            switchMap((traditionalResponse) =>
                              this.mipsApi
                                .savePIMeasurements(
                                  mvpPacketId,
                                  measurementRequest,
                                )
                                .pipe(
                                  map(() =>
                                    MipsPacketActions.savePIAttestationsSuccess({
                                      response: traditionalResponse,
                                      performanceStart: performanceStart.toISOString(),
                                      performanceEnd: performanceEnd.toISOString(),
                                    }),
                                  ),
                                ),
                            ),
                          ),
                      ),
                    ),
                ),
                catchError((error) => {
                  console.error(
                    "Error saving PI attestations (both mode):",
                    error,
                  );
                  return of(
                    MipsPacketActions.savePIAttestationsFailure({
                      error:
                        error.error?.title ||
                        error.message ||
                        "Failed to save PI attestations",
                    }),
                  );
                }),
              );
          }

          return this.mipsApi
            .savePIAttestations(
              packetId,
              questionnaireAttestations,
              organizationId,
              performanceYear,
            )
            .pipe(
              switchMap((attestationsResponse) => {
                return this.mipsApi
                  .savePIMeasurements(packetId, measurementRequest)
                  .pipe(
                    switchMap((measurementsResponse) =>
                      from([
                        MipsPacketActions.savePIAttestationsSuccess({
                          response: attestationsResponse,
                          performanceStart: performanceStart.toISOString(),
                          performanceEnd: performanceEnd.toISOString(),
                        }),
                        MipsPacketActions.savePIMeasurementsSuccess({ response: measurementsResponse, fullMeasures, exclusionIds }),
                      ])
                    ),
                  );
              }),
              catchError((error) => {
                console.error("Error saving PI attestations:", error);
                return of(
                  MipsPacketActions.savePIAttestationsFailure({
                    error:
                      error.error?.title ||
                      error.message ||
                      "Failed to save PI attestations",
                  }),
                );
              }),
            );
        },
      ),
    ),
  );

  savePIMeasurements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.savePIMeasurements),
      withLatestFrom(
        this.store.select(selectPacketId),
        this.store.select(
          (state) => (state as any).mipsPacket?.multiPacketMode,
        ),
      ),
      switchMap(
        ([
          { performanceStart, performanceEnd, measurements, fullMeasures, exclusionIds },
          packetId,
          multiPacketMode,
        ]) => {
          if (!packetId) {
            return of(
              MipsPacketActions.savePIMeasurementsFailure({
                error: "No submission ID available",
              }),
            );
          }

          const allMeasurements = [...measurements];

          const request = {
            performanceStart,
            performanceEnd,
            measurements: allMeasurements,
          };

          const traditionalPacketId = multiPacketMode?.traditionalPacketId;
          const mvpPacketId = multiPacketMode?.mvpPacketId;

          if (multiPacketMode?.enabled && traditionalPacketId && mvpPacketId) {
            return this.mipsApi
              .savePIMeasurements(traditionalPacketId, request)
              .pipe(
                switchMap((traditionalResponse) =>
                  this.mipsApi.savePIMeasurements(mvpPacketId, request).pipe(
                    map(() =>
                      MipsPacketActions.savePIMeasurementsSuccess({
                        response: traditionalResponse,
                        fullMeasures,
                        exclusionIds,
                      }),
                    ),
                  ),
                ),
                catchError((error) =>
                  of(
                    MipsPacketActions.savePIMeasurementsFailure({
                      error: error.message || "Failed to save PI measurements",
                    }),
                  ),
                ),
              );
          }

          return this.mipsApi.savePIMeasurements(packetId, request).pipe(
            map((response) =>
              MipsPacketActions.savePIMeasurementsSuccess({
                response,
                fullMeasures,
                exclusionIds,
              }),
            ),
            catchError((error) =>
              of(
                MipsPacketActions.savePIMeasurementsFailure({
                  error: error.message || "Failed to save PI measurements",
                }),
              ),
            ),
          );
        },
      ),
    ),
  );

  saveIAMeasurements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.saveIAMeasurements),
      mergeMap(
        ({
          packetId,
          performanceStart,
          performanceEnd,
          measurements,
        }) => {
          return this.mipsApi
            .saveIAMeasurements(
              packetId,
              performanceStart,
              performanceEnd,
              measurements,
            )
            .pipe(
              map((response) =>
                MipsPacketActions.saveIAMeasurementsSuccess({
                  response,
                  packetId,
                  measurements,
                  dateRangeStart: performanceStart.toISOString(),
                  dateRangeEnd: performanceEnd.toISOString(),
                }),
              ),
              catchError((error) =>
                of(
                  MipsPacketActions.saveIAMeasurementsFailure({
                    error:
                      error.message || "Failed to save improvement activities",
                  }),
                ),
              ),
            );
        },
      ),
    ),
  );

  updateDrcfStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.updateDRCFStatus),
      withLatestFrom(this.store.select(selectPacketId)),
      switchMap(([{ drcfStatus }, packetId]) => {
        if (!packetId) {
          return of(
            MipsPacketActions.updateDRCFStatusFailure({
              error: "No submission ID available",
            }),
          );
        }

        return this.mipsApi.updateDrcfStatus(packetId, drcfStatus).pipe(
          map(() => MipsPacketActions.updateDRCFStatusSuccess({ drcfStatus })),
          catchError((error) =>
            of(
              MipsPacketActions.updateDRCFStatusFailure({
                error: error.message || "Failed to update DRCF status",
              }),
            ),
          ),
        );
      }),
    ),
  );

  submitPacket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.submitPacket),
      switchMap(({ packetId }) =>
        this.mipsApi.submitPacket(packetId).pipe(
          map((response) => MipsPacketActions.submitPacketSuccess({ response })),
          catchError((error) => of(MipsPacketActions.submitPacketFailure({ error: parseProblemDetailsError(error, 'Failed to submit') }))),
        ),
      ),
    ),
  );

  submitPacketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.submitPacketSuccess),
      withLatestFrom(
        this.store.select(selectActivePacket),
        this.store.select(selectTinDetails),
      ),
      map(([{ response }, packet, tinDetails]) =>
        MipsPacketActions.setSubmissionSuccessData({
          packetId: packet!.id!,
          tin: tinDetails?.tin ?? '',
          npi: packet!.npi,
          isIndividual: packet!.reportingLevel === 'individual',
          performanceYear: packet!.performanceYear,
          externalSubmissionId: response.submissionId,
          submittedDate: response.submittedDate,
        }),
      ),
    ),
  );

  submitMultiplePackets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.submitMultiplePackets),
      switchMap(({ packetIds }) =>
        forkJoin(packetIds.map((id) => this.mipsApi.submitPacket(id))).pipe(
          map((responses) => MipsPacketActions.submitMultiplePacketsSuccess({ responses })),
          catchError((error) => of(MipsPacketActions.submitMultiplePacketsFailure({ error: parseProblemDetailsError(error, 'Failed to submit') }))),
        ),
      ),
    ),
  );

  handleSubmissionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          MipsPacketActions.submitPacketFailure,
          MipsPacketActions.submitMultiplePacketsFailure,
        ),
        tap(({ error }) => {
          const { message, details } = this.parseSubmissionError(error);
          this.errorDialogService.showCustom({
            title: 'Submission Error',
            message,
            details,
            icon: 'error',
            confirmButtonText: 'OK',
          });
        }),
      ),
    { dispatch: false },
  );

  handleScoreError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsPacketActions.getSubmissionScoreFailure),
        tap(({ error }) => {
          this.errorDialogService.showError(
            error || 'Failed to retrieve submission score. Please try again.',
            'Score Retrieval Error',
          );
        }),
      ),
    { dispatch: false },
  );

  // Catch-all for every other MipsPacket failure action — shows the shared error modal
  // instead of the old stepper error banner (removed; didn't fit the stepper layout).
  // submitPacketFailure/submitMultiplePacketsFailure (handleSubmissionError$) and
  // getSubmissionScoreFailure (handleScoreError$) already have their own dedicated
  // modal handling above and are intentionally excluded here to avoid a duplicate dialog.
  handleGenericPacketError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          MipsPacketActions.validateCEHRTIDFailure,
          MipsPacketActions.loadExistingPacketFailure,
          MipsPacketActions.loadBothPacketsFailure,
          MipsPacketActions.getOrCreatePacketFailure,
          MipsPacketActions.loadTinsForPracticeFailure,
          MipsPacketActions.saveTINDetailsFailure,
          MipsPacketActions.saveSettingsFailure,
          MipsPacketActions.saveQualityMeasurementsFailure,
          MipsPacketActions.savePIAttestationsFailure,
          MipsPacketActions.savePIMeasurementsFailure,
          MipsPacketActions.uploadPIDocumentFailure,
          MipsPacketActions.deletePIDocumentFailure,
          MipsPacketActions.saveIAMeasurementsFailure,
          MipsPacketActions.uploadIADocumentFailure,
          MipsPacketActions.deleteIADocumentFailure,
          MipsPacketActions.downloadIADocumentFailure,
          MipsPacketActions.loadDRCFEnvelopesFailure,
          MipsPacketActions.updateDRCFStatusFailure,
          MipsPacketActions.createDRCFEnvelopeFailure,
          MipsPacketActions.openDRCFSigningFailure,
          MipsPacketActions.uploadTINDocumentFailure,
          MipsPacketActions.deleteTINDocumentFailure,
          MipsPacketActions.approveTINFailure,
          MipsPacketActions.rejectTINFailure,
          MipsPacketActions.requestTINApprovalFailure,
          MipsPacketActions.loadOrgSubmissionsFailure,
          MipsPacketActions.deleteSubmissionFailure,
        ),
        tap(({ error }) => {
          this.errorDialogService.showError(
            error || 'An error occurred. Please try again.',
            'Error',
          );
        }),
      ),
    { dispatch: false },
  );

  getSubmissionScore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.getSubmissionScore),
      switchMap(({ submissionId }) =>
        this.mipsApi.getSubmissionScore(submissionId).pipe(
          map((score) => MipsPacketActions.getSubmissionScoreSuccess({ score })),
          catchError((error) => of(MipsPacketActions.getSubmissionScoreFailure({ error: error.error?.title || error.error?.message || error.message || 'Failed to retrieve score' }))),
        ),
      ),
    ),
  );

  // Auto-fetch the score whenever submission success data is set (initial submit or resubmit)
  autoFetchSubmissionScore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.setSubmissionSuccessData),
      map(({ packetId }) =>
        MipsPacketActions.getSubmissionScore({ submissionId: packetId }),
      ),
    ),
  );

  requestTinApproval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.requestTINApproval),
      switchMap(({ packetId }) =>
        this.mipsApi.requestTinApproval(packetId).pipe(
          map(() => MipsPacketActions.requestTINApprovalSuccess()),
          catchError((error) =>
            of(
              MipsPacketActions.requestTINApprovalFailure({
                error: error.message || "Failed to request TIN approval",
              }),
            ),
          ),
        ),
      ),
    ),
  );

  uploadTinDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.uploadTINDocument),
      withLatestFrom(
        this.store.select(selectActivePacket),
        this.store.select(
          (state) => (state as any).mipsPacket?.multiPacketMode,
        ),
      ),
      switchMap(([{ packetId, file }, currentPacket, multiPacketMode]) => {
        const traditionalPacketId = multiPacketMode?.traditionalPacketId;
        const mvpPacketId = multiPacketMode?.mvpPacketId;

        if (multiPacketMode?.enabled && traditionalPacketId && mvpPacketId) {
          return this.mipsApi.uploadTinDocument(traditionalPacketId, file).pipe(
            switchMap((traditionalResponse) =>
              this.mipsApi.uploadTinDocument(mvpPacketId, file).pipe(
                tap((mvpResponse) => {}),
                map((mvpResponse) =>
                  MipsPacketActions.uploadTINDocumentSuccess({
                    documentUrl: traditionalResponse.documentUrl,
                    documentName: traditionalResponse.fileName,
                  }),
                ),
              ),
            ),
            catchError((error) => {
              console.error("Upload FAILED:", error);
              return of(
                MipsPacketActions.uploadTINDocumentFailure({
                  error: error.message || "Failed to upload document",
                }),
              );
            }),
          );
        }

        return this.mipsApi.uploadTinDocument(packetId, file).pipe(
          map((response) =>
            MipsPacketActions.uploadTINDocumentSuccess({
              documentUrl: response.documentUrl,
              documentName: response.fileName,
            }),
          ),
          catchError((error) => {
            console.error("Upload FAILED:", error);
            return of(
              MipsPacketActions.uploadTINDocumentFailure({
                error: error.message || "Failed to upload document",
              }),
            );
          }),
        );
      }),
    ),
  );

  deleteTinDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.deleteTINDocument),
      withLatestFrom(
        this.store.select(selectActivePacket),
        this.store.select(
          (state) => (state as any).mipsPacket?.multiPacketMode,
        ),
      ),
      switchMap(([{ packetId }, currentPacket, multiPacketMode]) => {
        const traditionalPacketId = multiPacketMode?.traditionalPacketId;
        const mvpPacketId = multiPacketMode?.mvpPacketId;

        if (multiPacketMode?.enabled && traditionalPacketId && mvpPacketId) {
          return this.mipsApi.deleteTinDocument(traditionalPacketId).pipe(
            switchMap(() =>
              this.mipsApi
                .deleteTinDocument(mvpPacketId)
                .pipe(map(() => MipsPacketActions.deleteTINDocumentSuccess())),
            ),
            catchError((error) =>
              of(
                MipsPacketActions.deleteTINDocumentFailure({
                  error: error.message || "Failed to delete document",
                }),
              ),
            ),
          );
        }

        return this.mipsApi.deleteTinDocument(packetId).pipe(
          map(() => MipsPacketActions.deleteTINDocumentSuccess()),
          catchError((error) =>
            of(
              MipsPacketActions.deleteTINDocumentFailure({
                error: error.message || "Failed to delete document",
              }),
            ),
          ),
        );
      }),
    ),
  );

  approveTin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.approveTIN),
      withLatestFrom(
        this.store.select(selectActivePacket),
        this.store.select(
          (state) => (state as any).mipsPacket?.multiPacketMode,
        ),
      ),
      switchMap(([{ packetId }, currentPacket, multiPacketMode]) => {
        const traditionalPacketId = multiPacketMode?.traditionalPacketId;
        const mvpPacketId = multiPacketMode?.mvpPacketId;

        if (multiPacketMode?.enabled && traditionalPacketId && mvpPacketId) {
          return this.mipsApi.approveTin(traditionalPacketId).pipe(
            switchMap(() =>
              this.mipsApi.approveTin(mvpPacketId).pipe(
                map(() => {
                  const updatedTinDetails: TinEligibilityDetails = {
                    ...currentPacket!.tinDetails!,
                    tinStatus: "approved" as const,
                    approvedDate: new Date().toISOString(),
                  };

                  return MipsPacketActions.approveTINSuccess({
                    tinDetails: updatedTinDetails,
                  });
                }),
              ),
            ),
            catchError((error) =>
              of(
                MipsPacketActions.approveTINFailure({
                  error: error.message || "Failed to approve TIN",
                }),
              ),
            ),
          );
        }

        return this.mipsApi.approveTin(packetId).pipe(
          map(() => {
            const updatedTinDetails: TinEligibilityDetails = {
              ...currentPacket!.tinDetails!,
              tinStatus: "approved" as const,
              approvedDate: new Date().toISOString(),
            };

            return MipsPacketActions.approveTINSuccess({
              tinDetails: updatedTinDetails,
            });
          }),
          catchError((error) =>
            of(
              MipsPacketActions.approveTINFailure({
                error: error.message || "Failed to approve TIN",
              }),
            ),
          ),
        );
      }),
    ),
  );

  rejectTin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.rejectTIN),
      withLatestFrom(
        this.store.select(selectActivePacket),
        this.store.select(
          (state) => (state as any).mipsPacket?.multiPacketMode,
        ),
      ),
      switchMap(([{ packetId, reason }, currentPacket, multiPacketMode]) => {
        const traditionalPacketId = multiPacketMode?.traditionalPacketId;
        const mvpPacketId = multiPacketMode?.mvpPacketId;

        if (multiPacketMode?.enabled && traditionalPacketId && mvpPacketId) {
          return this.mipsApi.rejectTin(traditionalPacketId).pipe(
            switchMap(() =>
              this.mipsApi.rejectTin(mvpPacketId).pipe(
                map(() => {
                  const updatedTinDetails: TinEligibilityDetails = {
                    ...currentPacket!.tinDetails!,
                    tinStatus: "rejected" as const,
                    rejectionReason: reason,
                  };

                  return MipsPacketActions.rejectTINSuccess({
                    tinDetails: updatedTinDetails,
                  });
                }),
              ),
            ),
            catchError((error) =>
              of(
                MipsPacketActions.rejectTINFailure({
                  error: error.message || "Failed to reject TIN",
                }),
              ),
            ),
          );
        }

        return this.mipsApi.rejectTin(packetId).pipe(
          map(() => {
            const updatedTinDetails: TinEligibilityDetails = {
              ...currentPacket!.tinDetails!,
              tinStatus: "rejected" as const,
              rejectionReason: reason,
            };

            return MipsPacketActions.rejectTINSuccess({
              tinDetails: updatedTinDetails,
            });
          }),
          catchError((error) =>
            of(
              MipsPacketActions.rejectTINFailure({
                error: error.message || "Failed to reject TIN",
              }),
            ),
          ),
        );
      }),
    ),
  );

  validateCehrtId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.validateCEHRTID),
      switchMap(({ cehrtId }) =>
        this.mipsApi.validateCehrtId(cehrtId).pipe(
          map((isValid) =>
            MipsPacketActions.validateCEHRTIDSuccess({ isValid }),
          ),
          catchError((error) =>
            of(
              MipsPacketActions.validateCEHRTIDFailure({
                error: error.message || "Failed to validate CEHRT ID",
              }),
            ),
          ),
        ),
      ),
    ),
  );

  uploadPIDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.uploadPIDocument),
      switchMap(({ packetId, file }) => {
        return this.mipsApi.uploadPIDocument(packetId, file).pipe(
          map((response) =>
            MipsPacketActions.uploadPIDocumentSuccess({
              documentId: response.documentId,
              fileName: response.fileName,
              documentUrl: response.documentUrl,
            }),
          ),
          catchError((error) =>
            of(
              MipsPacketActions.uploadPIDocumentFailure({
                error: error.message || "Failed to upload PI document",
              }),
            ),
          ),
        );
      }),
    ),
  );

  downloadPIDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.downloadPIDocument),
      switchMap(({ packetId, documentId, documentName, asAttachment }) => {
        return this.mipsApi
          .downloadPIDocument(packetId, documentId, asAttachment)
          .pipe(
            map((blob) => {
              let fileName = "document.pdf";

              this.store
                .select(selectActivePacket)
                .pipe(take(1))
                .subscribe((currentPacket) => {
                  const piDoc =
                    currentPacket?.promotingInteroperability?.documents?.find(
                      (d) => d.documentId === documentId,
                    );
                  if (piDoc?.fileName) {
                    fileName = piDoc.fileName;
                  }
                });

              if (asAttachment) {
                downloadBlob(blob, fileName);
              }

              return MipsPacketActions.downloadPIDocumentSuccess({
                blob,
                fileName,
              });
            }),
            catchError((error) =>
              of(
                MipsPacketActions.downloadPIDocumentFailure({
                  error: error.message || "Failed to download PI document",
                }),
              ),
            ),
          );
      }),
    ),
  );

  viewPIDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.viewPIDocument),
      switchMap(({ packetId, documentId }) => {
        return this.mipsApi.viewPIDocument(packetId, documentId).pipe(
          map((blob) => {
            let fileName = "document.pdf";

            this.store
              .select(selectActivePacket)
              .pipe(take(1))
              .subscribe((currentPacket) => {
                const piDoc =
                  currentPacket?.promotingInteroperability?.documents?.find(
                    (d) => d.documentId === documentId,
                  );
                if (piDoc?.fileName) {
                  fileName = piDoc.fileName;
                }
              });

            openBlobInNewTab(blob);

            return MipsPacketActions.downloadPIDocumentSuccess({
              blob,
              fileName,
            });
          }),
          catchError((error) =>
            of(
              MipsPacketActions.downloadPIDocumentFailure({
                error: error.message || "Failed to view PI document",
              }),
            ),
          ),
        );
      }),
    ),
  );

  deletePIDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.deletePIDocument),
      switchMap(({ packetId, documentId }) => {
        return this.mipsApi.deletePIDocument(packetId, documentId).pipe(
          map(() => MipsPacketActions.deletePIDocumentSuccess({ documentId })),
          catchError((error) =>
            of(
              MipsPacketActions.deletePIDocumentFailure({
                error: error.message || "Failed to delete PI document",
              }),
            ),
          ),
        );
      }),
    ),
  );

  uploadIADocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.uploadIADocument),
      switchMap(({ packetId, measureId, file }) => {
        return this.mipsApi.uploadIADocument(packetId, measureId, file).pipe(
          map((response) =>
            MipsPacketActions.uploadIADocumentSuccess({
              packetId: packetId,
              documentId: response.documentId,
              fileName: response.fileName,
              measureId: response.measureId,
              documentUrl: response.documentUrl,
            }),
          ),
          catchError((error) =>
            of(
              MipsPacketActions.uploadIADocumentFailure({
                error: error.message || "Failed to upload IA document",
              }),
            ),
          ),
        );
      }),
    ),
  );

  downloadIADocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.downloadIADocument),
      switchMap(({ packetId, measureId, documentName, asAttachment }) => {
        return this.mipsApi
          .downloadIADocument(packetId, measureId, asAttachment)
          .pipe(
            map((blob) => {
              if (asAttachment) {
                downloadBlob(blob, documentName);
              }

              return MipsPacketActions.downloadIADocumentSuccess({
                blob,
                fileName: documentName,
              });
            }),
            catchError((error) =>
              of(
                MipsPacketActions.downloadIADocumentFailure({
                  error: error.message || "Failed to download IA document",
                }),
              ),
            ),
          );
      }),
    ),
  );

  viewIADocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.viewIADocument),
      switchMap(({ packetId, measureId }) => {
        return this.mipsApi.viewIADocument(packetId, measureId).pipe(
          map((blob) => {
            openBlobInNewTab(blob);

            return MipsPacketActions.downloadIADocumentSuccess({
              blob,
              fileName: "document.pdf",
            });
          }),
          catchError((error) =>
            of(
              MipsPacketActions.downloadIADocumentFailure({
                error: error.message || "Failed to view IA document",
              }),
            ),
          ),
        );
      }),
    ),
  );

  deleteIADocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.deleteIADocument),
      switchMap(({ packetId, measureId }) => {
        return this.mipsApi.deleteIADocument(packetId, measureId).pipe(
          map(() => MipsPacketActions.deleteIADocumentSuccess({ packetId, measureId })),
          catchError((error) =>
            of(
              MipsPacketActions.deleteIADocumentFailure({
                error: error.message || "Failed to delete IA document",
              }),
            ),
          ),
        );
      }),
    ),
  );

  createDrcfEnvelope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.createDRCFEnvelope),
      switchMap(({ submissionId, recipientEmail }) =>
        this.mipsApi
          .createDrcfEnvelope(submissionId, recipientEmail ?? "")
          .pipe(
            map((response) =>
              MipsPacketActions.createDRCFEnvelopeSuccess({
                envelopeId: response.envelopeId,
                submissionId,
              }),
            ),
            catchError((error) =>
              of(
                MipsPacketActions.createDRCFEnvelopeFailure({
                  error: error.error?.description
                    || error.error?.title
                    || "An unexpected error occurred while creating the DRCF agreement. Please try again.",
                }),
              ),
            ),
          ),
      ),
    ),
  );

  createDrcfEnvelopeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.createDRCFEnvelopeSuccess),
      map(({ submissionId }) =>
        MipsPacketActions.loadDRCFEnvelopes({ submissionId }),
      ),
    ),
  );

  createDrcfEnvelopeFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsPacketActions.createDRCFEnvelopeFailure),
        tap(({ error }) => {
          this.errorDialogService.showError(
            error || "An unexpected error occurred while creating the DRCF agreement. Please try again.",
            "Unable to Create Agreement"
          );
        })
      ),
    { dispatch: false }
  );

  voidDrcfEnvelopeFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsPacketActions.voidDRCFEnvelopeFailure),
        tap(({ error }) => {
          this.errorDialogService.showError(
            error || "An unexpected error occurred while voiding the DRCF agreement. Please try again.",
            "Unable to Void Agreement"
          );
        })
      ),
    { dispatch: false }
  );

  downloadDrcfDocumentFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsPacketActions.downloadDRCFDocumentFailure),
        tap(({ error }) => {
          this.errorDialogService.showError(
            error || "An unexpected error occurred while downloading the DRCF document. Please try again.",
            "Unable to Download Document"
          );
        })
      ),
    { dispatch: false }
  );

  downloadDrcfTemplateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsPacketActions.downloadDRCFTemplateFailure),
        tap(({ error }) => {
          this.errorDialogService.showError(
            error || "An unexpected error occurred while downloading the DRCF template. Please try again.",
            "Unable to Download Template"
          );
        })
      ),
    { dispatch: false }
  );

  loadDRCFEnvelopes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.loadDRCFEnvelopes),
      mergeMap(({ submissionId }) =>
        this.mipsApi.getDrcfEnvelopes(submissionId).pipe(
          map((documents) =>
            MipsPacketActions.loadDRCFEnvelopesSuccess({ submissionId, documents }),
          ),
          catchError((error) => {
            console.error("Error loading DRCF envelopes:", error);
            return of(
              MipsPacketActions.loadDRCFEnvelopesSuccess({ submissionId, documents: [] }),
            );
          }),
        ),
      ),
    ),
  );

  openDrcfSigning$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.openDRCFSigning),
      switchMap(({ envelopeId }) =>
        this.mipsApi.getDrcfRecipientView(envelopeId).pipe(
          map((response) => {
            return MipsPacketActions.openDRCFSigningSuccess({
              url: response.url,
            });
          }),
          catchError((httpError) => {
            console.error("DRCF signing error:", httpError);

            let errorMessage = "Failed to open document for signing";

            if (httpError.error?.error) {
              errorMessage = httpError.error.error;
            } else if (httpError.error?.title) {
              errorMessage = httpError.error.title;
            } else if (httpError.error?.detail) {
              errorMessage = httpError.error.detail;
            } else if (typeof httpError.error === "string") {
              errorMessage = httpError.error;
            }

            return of(
              MipsPacketActions.openDRCFSigningFailure({ error: errorMessage }),
            );
          }),
        ),
      ),
    ),
  );

  openDrcfSigningWindow$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(MipsPacketActions.openDRCFSigningSuccess),
        tap((action) => {
          if (action.url != null) {
            let signingWindow = window.open(action.url, "_blank");
            if (
              !signingWindow ||
              signingWindow.closed ||
              typeof signingWindow.closed == "undefined"
            ) {
              console.error("Popup was blocked");
            } else {
              signingWindow.focus();
            }
          } else {
            console.error("No URL provided");
          }
        }),
      );
    },
    { dispatch: false },
  );

  refreshDrcfAfterSigning$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MipsPacketActions.openDRCFSigningSuccess),
      withLatestFrom(this.store.select(selectPacketId)),
      filter(([_, packetId]) => packetId != null),
      delay(1000),
      map(([_, packetId]) =>
        MipsPacketActions.loadDRCFEnvelopes({ submissionId: packetId! }),
      ),
    );
  });

  voidDrcfEnvelope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.voidDRCFEnvelope),
      switchMap(({ submissionId, envelopeId, voidReason }) =>
        this.mipsApi
          .voidDrcfEnvelope(submissionId, envelopeId, voidReason)
          .pipe(
            switchMap(() =>
              this.mipsApi.getDrcfEnvelopes(submissionId).pipe(
                map((documents) => {
                  return MipsPacketActions.loadDRCFEnvelopesSuccess({
                    submissionId,
                    documents,
                  });
                }),
              ),
            ),
            catchError((error) =>
              of(
                MipsPacketActions.voidDRCFEnvelopeFailure({
                  error: error.message || "Failed to void envelope",
                }),
              ),
            ),
          ),
      ),
    ),
  );

  downloadDrcfDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.downloadDRCFDocument),
      switchMap(({ submissionId, envelopeId }) =>
        this.mipsApi.downloadDrcfSignedDocument(submissionId, envelopeId).pipe(
          map((blob) => {
            const fileName = `DRCF_${envelopeId}.pdf`;
            downloadBlob(blob, fileName);

            return MipsPacketActions.downloadDRCFDocumentSuccess({
              blob,
              fileName,
            });
          }),
          catchError((error) =>
            of(
              MipsPacketActions.downloadDRCFDocumentFailure({
                error: error.message || "Failed to download document",
              }),
            ),
          ),
        ),
      ),
    ),
  );

  downloadDrcfTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.downloadDRCFTemplate),
      switchMap(({ entityType }) =>
        this.mipsApi.downloadDrcfTemplate(entityType).pipe(
          map((blob) => {
            const fileName = `DRCF_Template_${entityType}.pdf`;
            downloadBlob(blob, fileName);

            return MipsPacketActions.downloadDRCFTemplateSuccess({
              blob,
              fileName,
            });
          }),
          catchError((error) =>
            of(
              MipsPacketActions.downloadDRCFTemplateFailure({
                error: error.message || "Failed to download DRCF template",
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadOrgSubmissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.loadOrgSubmissions),
      switchMap(({ orgId, performanceYear, tin }) =>
        this.mipsApi.getOrgSubmissions(orgId, performanceYear, tin).pipe(
          map((submissions) => {
            const mapped = submissions.map((s: any) => ({
              ...s,
              status: (s.status ?? s.submissionStatus ?? '').toLowerCase() || undefined,
              reportingType: s.reportingType ?? (s.programName?.toLowerCase().includes('mvp') ? 'mvp' : 'traditional'),
              reportingLevel: s.reportingLevel ?? s.entityType,
              lastUpdated: s.lastUpdated ?? s.modifyDate,
              clinicianName: s.clinicianName ?? null,
            }));
            return MipsPacketActions.loadOrgSubmissionsSuccess({ submissions: mapped });
          }),
          catchError((error) =>
            of(
              MipsPacketActions.loadOrgSubmissionsFailure({
                error: error.message || "Failed to load submissions",
              }),
            ),
          ),
        ),
      ),
    ),
  );

  deleteSubmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.deleteSubmission),
      switchMap(({ packetId, organizationId }) =>
        this.mipsApi.deleteSubmission(packetId, organizationId).pipe(
          map(() => MipsPacketActions.deleteSubmissionSuccess({ packetId })),
          catchError((error) =>
            of(
              MipsPacketActions.deleteSubmissionFailure({
                error: error.message || "Failed to delete submission",
              }),
            ),
          ),
        ),
      ),
    ),
  );

  handleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          MipsPacketActions.getOrCreatePacketFailure,
          MipsPacketActions.loadExistingPacketFailure,
          MipsPacketActions.saveTINDetailsFailure,
          MipsPacketActions.saveSettingsFailure,
          MipsPacketActions.savePIAttestationsFailure,
          MipsPacketActions.savePIMeasurementsFailure,
          MipsPacketActions.saveIAMeasurementsFailure,
          MipsPacketActions.updateDRCFStatusFailure,
          MipsPacketActions.submitPacketFailure,
          MipsPacketActions.requestTINApprovalFailure,
          MipsPacketActions.uploadTINDocumentFailure,
          MipsPacketActions.deleteTINDocumentFailure,
          MipsPacketActions.approveTINFailure,
          MipsPacketActions.rejectTINFailure,
          MipsPacketActions.uploadPIDocumentFailure,
          MipsPacketActions.deletePIDocumentFailure,
          MipsPacketActions.uploadIADocumentFailure,
          MipsPacketActions.deleteIADocumentFailure,
          MipsPacketActions.downloadIADocumentFailure,
          MipsPacketActions.deleteSubmissionFailure,
          MipsPacketActions.loadOrgSubmissionsFailure,
        ),
        tap(({ error }) => {
          console.error("MIPS Error:", error);
        }),
      ),
    { dispatch: false },
  );

  private mapBackendToFrontend(
    backendSubmission: any,
    eligibility?: EligibilitySelection,
    existingPacket?: MipsPacket | null,
    qualityMeasures?: any[],
  ): MipsPacket {

    const eligibilityData: EligibilitySelection = eligibility || {
      npi: backendSubmission.npi || "",
      practice: backendSubmission.organizationId?.toString() || "",
      performanceYear: backendSubmission.performanceYear,
      reportingType:
        backendSubmission.programName === "mips"
          ? "traditional"
          : backendSubmission.programName === "mvp"
            ? "mvp"
            : "traditional",
      reportingLevel: backendSubmission.entityType,
      reportingMethod: "",
    };

    const piDocuments = (backendSubmission.piDocuments || []).map((d: any) => ({
      documentId: d.documentId,
      fileName: d.fileName,
      documentUrl: d.documentUrl,
      uploadDate: d.uploadDate,
    }));

    const piAttestations = {
      piAuditAttestation: parseAttestationBool(backendSubmission.piAuditAttestation) ?? false,
      oncDirectReview: parseAttestationBool(backendSubmission.oncDirectReview),
      interoperabilityAttestation: parseAttestationBool(backendSubmission.interoperabilityAttestation),
      securityRiskAnalysis: parseAttestationBool(backendSubmission.securityRiskAnalysis),
      securityRiskAnalysisImplemented: parseAttestationBool(backendSubmission.securityRiskAnalysisImplemented),
      saferGuidesAttestation: parseAttestationBool(backendSubmission.saferGuidesAttestation),
    };

    let promotingInteroperability = this.mapPromotingInteroperability(
      backendSubmission.measurementSets || [],
      piDocuments,
      piAttestations,
    );

    if (!promotingInteroperability && piDocuments.length > 0) {
      promotingInteroperability = {
        cehrtValidationCount: 0,
        attested: false,
        attestations: piAttestations,
        objectivesMet: 0,
        measures: [],
        documents: piDocuments,
        estimatedPoints: 0,
        estimatedBonus: 0,
        estimatedTotal: 0,
        estimatedWeight: 0,
        maxWeight: 25,
      };
    }

    if (promotingInteroperability && backendSubmission.promotingInteroperability) {
      const apiPi = backendSubmission.promotingInteroperability;

      let measures = promotingInteroperability.measures;
      if (apiPi.measureScores?.length) {
        measures = measures.map((measure: any) => {
          const score = apiPi.measureScores.find(
            (s: any) => s.measureId === measure.measureId,
          );
          if (!score) return measure;
          return {
            ...measure,
            points: score.computedPoints,
            effectiveMaxPoints: score.effectiveMaxPoints,
            redistributedPoints: score.redistributedPoints,
            bonusPoints: score.bonusPoints,
            showBonusPoints: score.showBonusPoints,
          };
        });
      }

      promotingInteroperability = {
        ...promotingInteroperability,
        measures,
        estimatedPoints: apiPi.estimatedPoints ?? 0,
        estimatedBonus: apiPi.estimatedBonus ?? 0,
        estimatedTotal: apiPi.estimatedTotal ?? 0,
        estimatedWeight: apiPi.estimatedWeight ?? 0,
        maxWeight: apiPi.maxWeight ?? 25,
        allRequiredComplete: apiPi.allRequiredComplete ?? promotingInteroperability.allRequiredComplete,
        attestationsComplete: apiPi.attestationsComplete ?? false,
        incompleteMeasureIds: apiPi.incompleteMeasureIds ?? promotingInteroperability.incompleteMeasureIds,
      };
    }

    let improvementActivities = this.extractMeasurementSetCategory(
      backendSubmission.measurementSets,
      "ia",
      backendSubmission.iaDocuments,
      eligibilityData.reportingType,
    );

    if (!improvementActivities && backendSubmission.iaDocuments?.length > 0) {
      const iaActivities = backendSubmission.iaDocuments.map((doc: any) => {
        const measureIdMatch = doc.fileName.match(/^([A-Z_]+_[A-Z_]+_\d+)/);
        const measureId = measureIdMatch
          ? measureIdMatch[1]
          : doc.fileName.split("_").slice(0, 3).join("_");

        return {
          activityId: measureId,
          activityDescription: "",
          category: "",
          weight: "high" as const,
          points: 20,
          selected: false,
          documentId: doc.documentId,
          documentName: doc.name,
          documentUrl: doc.documentUrl,
        };
      });

      improvementActivities = {
        activities: iaActivities,
        dateRangeStart: backendSubmission.performanceStart,
        dateRangeEnd: backendSubmission.performanceEnd,
        estimatedPoints: 0,
        estimatedWeight: 0,
        maxWeight: 15,
        minimumActivitiesRequired: 1,
      };
    }

    if (improvementActivities && backendSubmission.improvementActivities) {
      const apiIA = backendSubmission.improvementActivities;

      let activities = improvementActivities.activities;
      if (apiIA.measureScores?.length) {
        const scoreMap = new Map<string, any>(apiIA.measureScores.map((s: any) => [s.measureId, s]));
        activities = activities.map((activity: any) => {
          const score = scoreMap.get(activity.activityId);
          if (!score) return activity;
          return { ...activity, points: score.points, isHighWeighted: score.isHighWeighted };
        });
      }

      improvementActivities = {
        ...improvementActivities,
        activities,
        estimatedPoints: apiIA.estimatedPoints ?? improvementActivities.estimatedPoints,
        estimatedWeight: apiIA.estimatedWeight ?? improvementActivities.estimatedWeight,
        maxWeight: apiIA.maxWeight ?? improvementActivities.maxWeight,
      };
    }

    let quality = this.extractMeasurementSetCategory(
      backendSubmission.measurementSets,
      "quality",
      undefined,
      eligibilityData.reportingType,
    );

    if (quality && backendSubmission.qualityScores) {
      const qs = backendSubmission.qualityScores;
      const isMvp = eligibilityData.reportingType === 'mvp';
      if (qs.measureScores?.length) {
        const scoreMap = new Map<string, any>(qs.measureScores.map((s: any) => [s.measureCode, s]));
        const applyScores = (measures: any[]) => measures.map((measure: any) => {
          const score = scoreMap.get(measure.measureId);
          if (!score) return measure;
          return { ...measure, calculatedPoints: score.achievementPoints };
        });
        quality = {
          ...quality,
          allMeasures: applyScores(quality.allMeasures ?? []),
          traditionalMeasures: applyScores(quality.traditionalMeasures ?? []),
          mvpMeasures: applyScores(quality.mvpMeasures ?? []),
        };
      }
      quality = {
        ...quality,
        estimatedPoints: (isMvp ? qs.mvpPoints : qs.traditionalPoints) ?? quality.estimatedPoints ?? 0,
        estimatedTotal: (isMvp ? qs.mvpTotal : qs.traditionalTotal) ?? quality.estimatedTotal ?? 0,
        estimatedWeight: (isMvp ? qs.mvpWeightedScore : qs.traditionalWeightedScore) ?? quality.estimatedWeight ?? 0,
        maxWeight: quality.maxWeight ?? 30,
      };
    }

    const packet: MipsPacket = {
      id: backendSubmission.submissionId,
      mvpId: backendSubmission.mvpid || undefined,
      orgId: backendSubmission.organizationId?.toString() || "",
      userId: "",
      performanceYear: backendSubmission.performanceYear,
      eligibility: eligibilityData,
      practice: backendSubmission.organizationId?.toString() || "",
      reportingType:
        backendSubmission.programName === "mips"
          ? "traditional"
          : backendSubmission.programName === "mvp"
            ? "mvp"
            : "traditional",
      reportingLevel: backendSubmission.entityType,
      npi: backendSubmission.npi || "",
      clinicianName: undefined,
      status: backendSubmission.submissionStatus?.toLowerCase() || "draft",
      createdDate: new Date().toISOString(),
      lastModifiedDate: new Date().toISOString(),
      submittedDate: backendSubmission.submittedDate ?? undefined,
      externalSubmissionId: backendSubmission.externalSubmissionId ?? undefined,
      relatedOrganizations: backendSubmission.relatedOrganizations ?? [],

      tinDetails: backendSubmission.tin
        ? {
            tin: backendSubmission.tin,
            confirmTin: backendSubmission.tin,
            practice: backendSubmission.organizationId?.toString() || "",
            performanceStartDate: backendSubmission.performanceStart,
            performanceEndDate: backendSubmission.performanceEnd,
            mipsEligible: true,
            emailForFeedback: backendSubmission.feedbackEmail || "",
            confirmEmailForFeedback: backendSubmission.feedbackEmail || "",
            selectedProviders: backendSubmission.providerIds || [],
            documentName: backendSubmission.documentName,
            documentUrl: backendSubmission.documentURL,
            tinStatus: backendSubmission.tinStatus?.toLowerCase() as
              | "pending"
              | "approved"
              | "rejected",
            approvedBy: backendSubmission.tinStatusUpdatedByUserID?.toString(),
            approvedDate: backendSubmission.tinStatusUpdatedDate?.toString(),
          }
        : undefined,

      settings: {
            cehrtId: backendSubmission.cehrtId || "",
            confirmCehrtId: backendSubmission.cehrtId || "",
          } as MipsSettings,

      quality,
      promotingInteroperability: promotingInteroperability,
      improvementActivities: improvementActivities,
      drcfStatus: existingPacket?.drcfStatus,

      step1Complete: true,
      step2Complete: true,
      step3Complete: !!backendSubmission.tin,
      step4Complete: !!backendSubmission.cehrtId,
      step5Complete: this.hasMeasurementSetCategory(
        backendSubmission.measurementSets,
        "quality",
      ),
      step6Complete: this.hasMeasurementSetCategory(
        backendSubmission.measurementSets,
        "pi",
      ),
      step7Complete: this.hasMeasurementSetCategory(
        backendSubmission.measurementSets,
        "ia",
      ),
      step8Complete: existingPacket?.drcfStatus?.allDocumentsSigned ?? false,

      canSubmit: false,
    };

    if (packet.quality && qualityMeasures && qualityMeasures.length > 0) {
      packet.quality = enrichQualityData(packet.quality, qualityMeasures);
    }
    return packet;
  }

  private extractMeasurementSetCategory(
    measurementSets: any[],
    category: string,
    documents?: any[],
    reportingType?: string,
  ): any {
    if (category === "quality") {
      const sets = measurementSets?.filter((ms) => ms.category === category);
      if (!sets || sets.length === 0) return undefined;
      const mergedMeasurements = sets.flatMap((ms) => ms.measurements || []);
      const firstSet = sets[0];
      return this.mapQualityCategory({ ...firstSet, measurements: mergedMeasurements }, reportingType);
    }

    const set = measurementSets?.find((ms) => ms.category === category);
    if (!set) return undefined;

    if (category === "ia") {
      return this.mapImprovementActivities(set, documents, reportingType);
    }

    return undefined;
  }

  private mapQualityCategory(measurementSet: any, reportingType?: string): any {
    const isMvp = reportingType === "mvp";

    const measures = (measurementSet.measurements || []).map((m: any) => ({
      measureId: m.measureId,
      measureTitle: "",
      isOutcome: false,
      isHighPriority: false,
      isMvpMeasure: isMvp,
      numeratorDenominator:
        m.numerator && m.denominator ? `${m.numerator}/${m.denominator}` : "",
      denominatorExclusion: m.denominatorExclusion,
      denominatorException: m.denominatorException,
      performance:
        m.numerator && m.denominator ? (m.numerator / m.denominator) * 100 : 0,
      points: 0,
      selected: true,
      denominator: m.denominator,
      inverse: false,
    }));

    return {
      traditionalMeasures: isMvp ? [] : measures,
      traditionalMeasuresSelected: isMvp ? 0 : measures.length,
      mvpMeasures: isMvp ? measures : [],
      mvpMeasuresSelected: isMvp ? measures.length : 0,
      allMeasures: measures,
      selectedMeasureIds: measures.map((m: any) => m.measureId),
      minimumMeasuresRequired: isMvp ? 4 : 6,
      hasOutcomeHighPriority: false,
      meetsCaseMinimum: false,
      measureCollectionType: measurementSet.submissionMethod,
    };
  }

  private mapPromotingInteroperability(
    measurementSets: any[],
    piDocuments?: any[],
    piAttestations?: any,
  ): any {
    const piMeasurementSet = measurementSets?.find(
      (ms) => ms.category === "pi",
    );

    if (!piMeasurementSet) {
      return undefined;
    }

    const allMeasurements = piMeasurementSet?.measurements || [];

    const knownExclusionIds = [
      "PI_LVPP_1",
      "PI_EP_2_EX_1",
      "PI_EP_2_EX_2",
      "PI_LVOTC_1",
      "PI_LVITC_2",
      "PI_PHCDRR_1_EX_1",
      "PI_PHCDRR_1_EX_2",
      "PI_PHCDRR_1_EX_3",
      "PI_PHCDRR_3_EX_1",
      "PI_PHCDRR_3_EX_2",
      "PI_PHCDRR_3_EX_3",
    ];

    const exclusionMeasurements = allMeasurements.filter((m: any) =>
      knownExclusionIds.includes(m.measureId),
    );

    const allExclusionIds = exclusionMeasurements.map(
      (ex: any) => ex.measureId,
    );

    const regularMeasurements = allMeasurements.filter((m: any) => {
      const isEngagement =
        m.measureId.endsWith("_PRE") || m.measureId.endsWith("_PROD");
      const isExclusion = knownExclusionIds.includes(m.measureId);
      return !isEngagement && !isExclusion;
    });

    const measures = regularMeasurements.map((m: any) => {
      const baseMeasureId = m.measureId;

      const preMeasure = allMeasurements.find(
        (ms: any) => ms.measureId === `${baseMeasureId}_PRE`,
      );
      const prodMeasure = allMeasurements.find(
        (ms: any) => ms.measureId === `${baseMeasureId}_PROD`,
      );

      let activeEngagementLevel:
        | "preProduction"
        | "validatedProduction"
        | null = null;
      if (preMeasure?.booleanValue) {
        activeEngagementLevel = "preProduction";
      } else if (prodMeasure?.booleanValue) {
        activeEngagementLevel = "validatedProduction";
      }

      let completed = false;
      let attestation = undefined;
      let numerator = undefined;
      let denominator = undefined;
      let performance = 0;

      switch (m.valueType) {
        case "Boolean":
          attestation = m.booleanValue;
          completed = m.booleanValue === true;
          if (activeEngagementLevel) {
            completed = completed && !!activeEngagementLevel;
          }
          break;

        case "Proportion":
          numerator = m.numerator;
          denominator = m.denominator;
          performance =
            numerator && denominator ? (numerator / denominator) * 100 : 0;
          completed = (numerator || 0) > 0 && (denominator || 0) > 0;
          break;
      }

      return {
        measureId: baseMeasureId,
        attestation: attestation,
        numerator: numerator,
        denominator: denominator,
        performance: performance,
        activeEngagementLevel: activeEngagementLevel,
        exclusionClaimed: null,
        completed: completed,
      };
    });

    const documents = (piDocuments || []).map((d: any) => ({
      documentId: d.documentId,
      fileName: d.fileName,
      documentUrl: d.documentUrl,
      uploadDate: d.uploadDate,
    }));

    return {
      cehrtValidationCount: 0,
      attested: piAttestations?.piAuditAttestation ?? false,
      attestations: {
        piAuditAttestation: piAttestations?.piAuditAttestation ?? false,
        oncDirectReview: piAttestations?.oncDirectReview ?? null,
        interoperabilityAttestation:
          piAttestations?.interoperabilityAttestation ?? null,
        securityRiskAnalysis: piAttestations?.securityRiskAnalysis ?? null,
        securityRiskAnalysisImplemented: piAttestations?.securityRiskAnalysisImplemented ?? null,
        saferGuidesAttestation: piAttestations?.saferGuidesAttestation ?? null,
      },
      objectivesMet: 0,
      measures: measures,
      documents: documents,
      allExclusionIds: allExclusionIds,
      performanceStart: piMeasurementSet?.performanceStart || null,
      performanceEnd: piMeasurementSet?.performanceEnd || null,
    };
  }

  private mapImprovementActivities(
    measurementSet: any,
    iaDocuments?: any[],
    reportingType?: string,
  ): any {
    const activities = (measurementSet.measurements || []).map((m: any) => {
      const activityDoc = (iaDocuments || []).find((d: any) =>
        d.fileName.startsWith(`${m.measureId}_`),
      );

      return {
        activityId: m.measureId,
        activityDescription: "",
        category: "",
        weight: "high" as const,
        points: 20,
        selected: m.booleanValue ?? true,
        documentId: activityDoc?.documentId,
        documentName: activityDoc?.name,
        documentUrl: activityDoc?.documentUrl,
      };
    });

    const orphanedDocuments = (iaDocuments || []).filter((doc: any) => {
      const measureIdMatch = doc.fileName.match(/^([A-Z_]+_[A-Z_]+_\d+)/);
      const measureId = measureIdMatch ? measureIdMatch[1] : null;

      if (!measureId) return false;

      const exists = activities.some((a: any) => a.activityId === measureId);
      return !exists;
    });

    orphanedDocuments.forEach((doc: any) => {
      const measureIdMatch = doc.fileName.match(/^([A-Z_]+_[A-Z_]+_\d+)/);
      const measureId = measureIdMatch ? measureIdMatch[1] : null;

      if (measureId) {
        activities.push({
          activityId: measureId,
          activityDescription: "",
          category: "",
          weight: "high" as const,
          points: 20,
          selected: false,
          documentId: doc.documentId,
          documentName: doc.name,
          documentUrl: doc.documentUrl,
        });
      }
    });

    const selectedActivities = activities.filter((a: any) => a.selected);
    const pointsPerActivity = reportingType === 'mvp' ? 40 : 20;
    const basePoints = selectedActivities.length * pointsPerActivity;
    const estimatedPoints = Math.min(basePoints, 40);

    return {
      activities: activities,
      dateRangeStart: measurementSet.performanceStart,
      dateRangeEnd: measurementSet.performanceEnd,
      estimatedPoints,
      estimatedWeight: Math.round((estimatedPoints / 40) * 15 * 100) / 100,
      maxWeight: 15,
      minimumActivitiesRequired: 1,
    };
  }

  private hasMeasurementSetCategory(
    measurementSets: any[],
    category: string,
  ): boolean {
    return measurementSets?.some((ms) => ms.category === category) ?? false;
  }

  autoLoadBothPackets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.setMultiPacketMode),
      withLatestFrom(this.store.select((state: any) => state.mipsPacket)),
      filter(([{ traditionalPacketId, mvpPacketId }, packetState]) => {
        const hasPacketIds = !!traditionalPacketId && !!mvpPacketId;

        const traditionalAlreadyLoaded =
          packetState?.traditionalPacket?.id === traditionalPacketId;
        const mvpAlreadyLoaded =
          packetState?.mvpPacket?.id === mvpPacketId;
        const bothAlreadyLoaded = traditionalAlreadyLoaded && mvpAlreadyLoaded;
        return hasPacketIds && !bothAlreadyLoaded;
      }),
      map(([{ traditionalPacketId }]) => {
        return MipsPacketActions.loadExistingPacket({
          packetId: traditionalPacketId!,
        });
      }),
    ),
  );

  syncSharedDataOnBothMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.setMultiPacketMode),
      withLatestFrom(this.store.select((state: any) => state.mipsPacket)),
      filter(([action, state]) => {
        return state.sharedDataSyncNeeded === true;
      }),
      mergeMap(([action, state]) => {
        const traditionalPacket = state.traditionalPacket;
        const mvpPacket = state.mvpPacket;
        const mvpPacketId = action.mvpPacketId;

        if (!mvpPacketId || !traditionalPacket || !mvpPacket) {
          return [{ type: '[MipsPacket] Sync Shared Data Skipped' }];
        }

        const actions: any[] = [];

        if (traditionalPacket.tinDetails) {
          actions.push(
            MipsPacketActions.saveTINDetails({
              packetId: mvpPacketId,
              tinDetails: traditionalPacket.tinDetails,
            })
          );
        }

        if (traditionalPacket.settings) {
          actions.push(
            MipsPacketActions.saveSettings({
              settings: traditionalPacket.settings,
            })
          );
        }

        if (traditionalPacket.promotingInteroperability) {
          const piData = traditionalPacket.promotingInteroperability as any;

          const { MIN_DATE, MAX_DATE } = getMipsPerformanceDates();
          const performanceStart = piData.performanceStart
            ? new Date(piData.performanceStart)
            : MIN_DATE;
          const performanceEnd = piData.performanceEnd
            ? new Date(piData.performanceEnd)
            : MAX_DATE;

          const measurements = piData.measures?.map((measure: any) => ({
            measureId: measure.measureId,
            valueType: measure.valueType || 'Boolean',
            booleanValue: measure.booleanValue,
            numeratorValue: measure.numeratorValue,
            denominatorValue: measure.denominatorValue,
          })) || [];

          actions.push(
            MipsPacketActions.savePIMeasurements({
              packetId: mvpPacketId,
              performanceStart,
              performanceEnd,
              measurements,
              fullMeasures: piData.measures || [],
              exclusionIds: piData.allExclusionIds || [],
            })
          );
        }

        actions.push({ type: '[MipsPacket] Sync Shared Data Completed' });

        return actions;
      })
    )
  );

  // ============================================
  // QUALITY MEASURES
  // ============================================

  loadQualityMeasures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.loadQualityMeasures),
      switchMap(({ orgId, filter }) =>
        this.dashboardService.getMeasureSummaries(orgId, filter).pipe(
          map((dashboardMeasures) => {
            const measures = applyYearRetirements(
              dashboardMeasures.map((m: DashboardMeasureDetailProd) => {
                if (m.populationCount == null) return m;
                return {
                  ...m,
                  isMultiPerformance: true,
                  isMultiPerformanceParent: m.populationNbr == null,
                  showChild: false,
                  expanded: false,
                };
              }),
              filter.measureYear
            );
            return MipsPacketActions.loadQualityMeasuresSuccess({ measures });
          }),
          catchError((error) =>
            of(MipsPacketActions.loadQualityMeasuresFailure({
              error: error.message || 'Failed to load quality measures',
            }))
          )
        )
      )
    )
  );

  triggerQualityMeasureLoadOnPacket$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(MipsPacketActions.getOrCreatePacketSuccess, MipsPacketActions.loadExistingPacketSuccess),
        map((action) => action.packet)
      ),
      this.actions$.pipe(
        ofType(MipsPacketActions.loadBothPacketsSuccess),
        map((action) => action.traditionalPacket)
      )
    ).pipe(
      withLatestFrom(this.store.select(selectProvidersForSelectedPractice)),
      map(([packet, providers]) => {
        const orgId = parseInt(packet.orgId, 10);
        const clinician = this.buildClinicianFilter(packet, undefined, providers);
        return MipsPacketActions.loadQualityMeasures({
          orgId,
          filter: { clinician, location: ['all'], measureYear: packet.performanceYear },
        });
      })
    )
  );

  reloadQuestionnaireForPacketOrg$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.getOrCreatePacketSuccess, MipsPacketActions.loadExistingPacketSuccess),
      withLatestFrom(this.store.select(selectActiveUser), this.store.select(selectMipsQuestionnaire)),
      filter(([, user]) => !!user?.orgId),
      mergeMap(([action, user, globalQuestionnaire]) => {
        if (action.packet.orgId !== user!.orgId.toString()) {
          return of(MipsPacketActions.loadPacketOrgQuestionnaire({
            orgId: action.packet.orgId,
            performanceYear: action.packet.performanceYear,
          }));
        }
        const globalOrgId = globalQuestionnaire?.organizationID?.toString();
        if (globalOrgId && globalOrgId !== user!.orgId.toString()) {
          return of(MipsQuestionnaireActions.loadMIPSQuestionnaire({
            orgId: user!.orgId.toString(),
            performanceYear: action.packet.performanceYear,
          }));
        }
        return EMPTY;
      })
    )
  );

  loadPacketOrgQuestionnaire$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.loadPacketOrgQuestionnaire),
      switchMap(({ orgId, performanceYear }) =>
        this.questionnaireService.getMipsQuestionnaire(orgId, performanceYear).pipe(
          map((questionnaire) =>
            MipsPacketActions.loadPacketOrgQuestionnaireSuccess({ questionnaire })
          ),
          catchError((error) =>
            of(MipsPacketActions.loadPacketOrgQuestionnaireFailure({ error: error.message || 'Failed to load questionnaire' }))
          )
        )
      )
    )
  );

  // Refreshes reweighting whenever the effective questionnaire changes (initial load, org-switch
  // reload, or a hardship-answer edit) so it always reflects the same source of truth the API's
  // MipsReweightingService already owns — no local scenario-table computation on the frontend.
  triggerMipsReweightingLoad$ = createEffect(() =>
    merge(
      this.actions$.pipe(ofType(MipsQuestionnaireActions.loadMIPSQuestionnaireSuccess)),
      this.actions$.pipe(ofType(MipsQuestionnaireActions.updateMIPSQuestionnaireSuccess)),
      this.actions$.pipe(ofType(MipsPacketActions.loadPacketOrgQuestionnaireSuccess)),
    ).pipe(
      withLatestFrom(this.store.select(selectEffectiveQuestionnaire)),
      filter(([, questionnaire]) => !!questionnaire?.organizationID && !!questionnaire?.measurementYear),
      map(([, questionnaire]) =>
        MipsPacketActions.loadMipsReweighting({
          orgId: String(questionnaire!.organizationID),
          performanceYear: questionnaire!.measurementYear!,
        })
      ),
    )
  );

  reloadQualityMeasuresOnQuestionnaireSave$ = createEffect(() =>
    merge(
      this.actions$.pipe(ofType(MipsQuestionnaireActions.submitMIPSQuestionnaireSuccess)),
      this.actions$.pipe(ofType(MipsQuestionnaireActions.updateMIPSQuestionnaireSuccess)),
    ).pipe(
      withLatestFrom(this.store.select(selectActivePacket), this.store.select(selectProvidersForSelectedPractice)),
      filter(([, packet]) => !!packet),
      map(([, packet, providers]) => {
        const orgId = parseInt(packet!.orgId, 10);
        const clinician = this.buildClinicianFilter(packet!, undefined, providers);
        return MipsPacketActions.loadQualityMeasures({
          orgId,
          filter: { clinician, location: ['all'], measureYear: packet!.performanceYear },
        });
      }),
    )
  );

  loadMipsReweighting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.loadMipsReweighting),
      switchMap(({ orgId, performanceYear }) =>
        this.questionnaireService.getMipsReweighting(orgId, performanceYear).pipe(
          map((data) => MipsPacketActions.loadMipsReweightingSuccess({ data })),
          catchError((error) =>
            of(MipsPacketActions.loadMipsReweightingFailure({ error: error?.message ?? 'Failed to load reweighting' }))
          )
        )
      )
    )
  );

  triggerQualityMeasureLoadOnTinSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsPacketActions.saveTINDetailsSuccess),
      withLatestFrom(
        this.store.select(selectActivePacket),
        this.store.select(selectLastQualityMeasureClinicians),
        this.store.select(selectProvidersForSelectedPractice),
      ),
      filter(([{ tinDetails }, packet, lastClinicians, providers]) => {
        if (!packet) return false;
        const newClinicians = this.buildClinicianFilter(packet, tinDetails, providers);
        return (
          JSON.stringify([...newClinicians].sort()) !==
          JSON.stringify([...(lastClinicians ?? [])].sort())
        );
      }),
      map(([{ tinDetails }, packet, , providers]) => {
        const orgId = parseInt(packet!.orgId, 10);
        const clinician = this.buildClinicianFilter(packet!, tinDetails, providers);
        return MipsPacketActions.loadQualityMeasures({
          orgId,
          filter: { clinician, location: ['all'], measureYear: packet!.performanceYear },
        });
      })
    )
  );

  private buildClinicianFilter(
    packet: MipsPacket,
    tinDetails?: TinEligibilityDetails,
    providers?: DashboardProviderProd[],
  ): string[] {
    const details = tinDetails ?? packet.tinDetails;

    if (packet.reportingLevel !== 'individual') {
      const selected = details?.selectedProviders;
      if (selected && selected.length > 0) {
        return selected.map((id) => {
          const match = providers?.find((p) => p.id === Number(id));
          return match?.npi ?? id.toString();
        });
      }
      return ['all'];
    }
    
    const match = providers?.find((p) => p.npi === packet.npi);
    return match?.npi ? [match.npi] : ['all'];
  }

  private parseSubmissionError(error: string): { message: string; details?: string } {
    const jsonMatch = error?.match(/\{.*\}$/s);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]);
        const qppError = parsed?.error;
        if (qppError) {
          const detailMessages = qppError.details
            ?.map((d: any) => d.message)
            .filter(Boolean)
            .join('\n');
          return {
            message: 'Your submission was rejected by CMS due to a validation error.',
            details: detailMessages || qppError.message,
          };
        }
      } catch {
        // fall through
      }
    }
    return { message: error || 'An error occurred while submitting. Please try again.' };
  }
}
